/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        useAuthorization.ts
 * @description Hook de gestion des autorisations basees sur les roles
 *              Centralise la logique de verification des permissions utilisateur
 *
 * @roles       ROLE_ADMIN              - Administrateur systeme (acces total)
 *              ROLE_GID_REGUL          - Regulateur GID
 *              ROLE_REGULATEUR         - Module Regulation
 *              ROLE_CHEF_DENEIGNEMENT  - Chef d'equipe
 *              ROLE_AGENT_DENEIGNEMENT - Agent terrain
 *              ROLE_COMPAGNIE          - Compagnie aerienne
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useAuthStore } from '@/store/authStore';

/**
 * Hook de gestion des autorisations
 *
 * Fournit des fonctions utilitaires pour verifier les roles de l'utilisateur
 * et determiner l'acces aux differentes sections de l'application.
 *
 * @returns Objet contenant les fonctions de verification et l'utilisateur
 *
 * @example
 * const { hasRole, canAccess } = useAuthorization();
 * if (canAccess.admin) { ... }
 * if (hasRole('ROLE_GID_REGUL')) { ... }
 */
export function useAuthorization() {
  const user = useAuthStore((state) => state.user);

  /**
   * Verifie si l'utilisateur possede un role specifique
   * @param role - Le role a verifier
   */
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) ?? false;
  };

  /**
   * Verifie si l'utilisateur possede au moins un des roles specifies
   * @param roles - Liste des roles acceptes
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(hasRole);
  };

  /**
   * Verifie si l'utilisateur possede tous les roles specifies
   * @param roles - Liste des roles requis
   */
  const hasAllRoles = (roles: string[]): boolean => {
    return roles.every(hasRole);
  };

  /**
   * Objet de permissions pre-calculees pour les sections principales
   */
  const canAccess = {
    /** Acces a la section Administration */
    admin: hasRole('ROLE_ADMIN'),

    /** Acces au module Regulation */
    regulation: hasAnyRole(['ROLE_ADMIN', 'ROLE_REGULATEUR', 'ROLE_GID_REGUL']),

    /** Acces au Planning des vacations */
    planning: hasAnyRole(['ROLE_ADMIN', 'ROLE_CHEF_DENEIGNEMENT']),

    /** Acces aux Interventions (lecture/creation) */
    interventions: hasAnyRole(['ROLE_ADMIN', 'ROLE_GID_REGUL', 'ROLE_CHEF_DENEIGNEMENT', 'ROLE_REGULATEUR']),

    /** Acces a l'interface mobile agent */
    mobile: hasAnyRole(['ROLE_AGENT_DENEIGNEMENT', 'ROLE_CHEF_DENEIGNEMENT']),

    /** Acces aux exports et rapports */
    exports: hasAnyRole(['ROLE_ADMIN', 'ROLE_GID_REGUL', 'ROLE_REGULATEUR']),

    /** Acces a la gestion des utilisateurs */
    users: hasRole('ROLE_ADMIN'),

    /** Acces a la gestion des equipes */
    teams: hasAnyRole(['ROLE_ADMIN', 'ROLE_CHEF_DENEIGNEMENT']),

    /**
     * Creation d'une demande de deneigement (bouton "Nouveau deneignement").
     * Autorise par defaut pour ROLE_GID_REGUL (et ROLE_ADMIN par heritage) ;
     * ROLE_COMPAGNIE ne l'est que si son drapeau
     * User.autoriseCreationIntervention est actif (optionnable a la creation
     * de l'utilisateur - cf backend DeneignementController::create()).
     */
    creationDeneignement:
      hasAnyRole(['ROLE_ADMIN', 'ROLE_GID_REGUL'])
      || (hasRole('ROLE_COMPAGNIE') && user?.autoriseCreationIntervention === true),

    /**
     * Actions de gestion sur une intervention existante (affecter/changer
     * une equipe, modifier, annuler, gerer les zones/jalons...). ROLE_COMPAGNIE
     * n'a qu'un role de visualisation sur les interventions : meme avec
     * creationDeneignement actif (qui n'autorise que la creation), il ne doit
     * jamais voir ces boutons de gestion - deja impossibles cote backend
     * (IsGranted ROLE_CHEF_DENEIGNEMENT/ROLE_GID_REGUL sur ces routes, que
     * ROLE_COMPAGNIE n'a jamais).
     */
    gestionDeneignement: !hasRole('ROLE_COMPAGNIE') || hasRole('ROLE_ADMIN'),
  };

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    canAccess,
    user,
  };
}

export default useAuthorization;
